import { useState } from "react";
import { api, formatApiError } from "../lib/api";
import { Mail, MapPin, Clock, Send, CheckCircle2, Twitter, Instagram } from "lucide-react";
import { toast } from "sonner";

const SUBJECTS = [
    "Product inquiry",
    "Order status",
    "COA / batch documentation",
    "Bulk / wholesale order",
    "Manufacturing partnership",
    "Press / media",
    "Other",
];

export default function ContactPage() {
    const [form, setForm] = useState({ name: "", email: "", subject: SUBJECTS[0], message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            setSent(true);
            setForm({ name: "", email: "", subject: SUBJECTS[0], message: "" });
            toast.success("Message received");
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setLoading(false); }
    };

    return (
        <div data-testid="contact-page" className="pt-48 pb-24 bg-[var(--bg)] min-h-screen">
            <div className="max-w-7xl mx-auto px-6 lg:px-10">
                <header className="mb-16 max-w-3xl">
                    <div className="data-label mb-3">CONTACT</div>
                    <h1 className="font-display text-5xl md:text-6xl lg:text-7xl leading-[0.95] tracking-tight mb-6">
                        Get in touch with<br/><span className="text-[var(--brand)]">our research team.</span>
                    </h1>
                    <p className="text-lg text-secondary leading-relaxed">
                        Have a question about a research compound, a Certificate of Analysis, or a bulk laboratory order?
                        Send us a message and we'll respond within one business day.
                    </p>
                </header>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
                    {/* Form */}
                    <div className="lg:col-span-7">
                        <div className="card p-8 lg:p-10">
                            {sent ? (
                                <div className="text-center py-10" data-testid="contact-success">
                                    <CheckCircle2 size={48} className="text-[var(--success)] mx-auto mb-5" />
                                    <h2 className="font-display text-3xl mb-3">Message received.</h2>
                                    <p className="text-secondary mb-6 max-w-sm mx-auto">
                                        Thanks for reaching out. Our research team will respond to your inquiry within one business day.
                                    </p>
                                    <button onClick={() => setSent(false)} className="btn btn-outline" data-testid="contact-send-another">
                                        Send Another Message
                                    </button>
                                </div>
                            ) : (
                                <form onSubmit={submit} className="space-y-5">
                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                        <div>
                                            <label className="label">Full Name</label>
                                            <input
                                                value={form.name}
                                                onChange={(e) => setForm({ ...form, name: e.target.value })}
                                                required
                                                placeholder="Dr. Jane Smith"
                                                className="input"
                                                data-testid="contact-name"
                                            />
                                        </div>
                                        <div>
                                            <label className="label">Email</label>
                                            <input
                                                value={form.email}
                                                onChange={(e) => setForm({ ...form, email: e.target.value })}
                                                required
                                                type="email"
                                                placeholder="researcher@laboratory.com"
                                                className="input"
                                                data-testid="contact-email"
                                            />
                                        </div>
                                    </div>
                                    <div>
                                        <label className="label">Subject</label>
                                        <select
                                            value={form.subject}
                                            onChange={(e) => setForm({ ...form, subject: e.target.value })}
                                            className="input"
                                            data-testid="contact-subject"
                                        >
                                            {SUBJECTS.map((s) => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                    <div>
                                        <label className="label">Message</label>
                                        <textarea
                                            value={form.message}
                                            onChange={(e) => setForm({ ...form, message: e.target.value })}
                                            required
                                            rows={6}
                                            placeholder="Tell us what you're researching and how we can help…"
                                            className="input resize-none"
                                            data-testid="contact-message"
                                        />
                                    </div>
                                    <button type="submit" disabled={loading} className="btn btn-solid w-full sm:w-auto" data-testid="contact-submit">
                                        {loading ? "Sending…" : <>Send Message <Send size={14} /></>}
                                    </button>
                                    <p className="data-label text-[0.65rem] leading-relaxed">
                                        By submitting this form you confirm that all communications will pertain to legitimate laboratory research only.
                                    </p>
                                </form>
                            )}
                        </div>
                    </div>

                    {/* Sidebar info */}
                    <aside className="lg:col-span-5 space-y-4">
                        <InfoCard
                            tile="tile-cream"
                            icon={Mail}
                            label="Email"
                            value="support@aminoplex.bio"
                            sub="For product, COA, and order inquiries"
                        />
                        <InfoCard
                            tile="tile-blue"
                            icon={Clock}
                            label="Hours"
                            value="Mon – Fri · 9 AM – 6 PM ET"
                            sub="Inquiries are answered within 24 business hours"
                        />
                        <InfoCard
                            tile="tile-pink"
                            icon={MapPin}
                            label="Mailing Address"
                            value={<span className="whitespace-pre-line">{"JNA Ventures LLC\n(d/b/a Aminoplex)\nP.O. Box 491\nHaymarket, VA 20168, USA"}</span>}
                            sub="Written correspondence and returns (RMA required — email first)"
                        />

                        <div className="card p-6">
                            <div className="font-display font-bold text-base mb-3 text-[var(--text-primary)]">Connect</div>
                            <p className="text-sm text-secondary mb-4">Follow Aminoplex for product launches, new COAs, and research education.</p>
                            <div className="flex items-center gap-2">
                                <a href="#" className="btn"><Twitter size={14} /> Twitter</a>
                                <a href="#" className="btn"><Instagram size={14} /> Instagram</a>
                            </div>
                        </div>

                        <div className="card p-6">
                            <div className="font-display font-bold text-base mb-2 text-[var(--text-primary)]">Bulk &amp; Wholesale</div>
                            <p className="text-sm text-secondary leading-relaxed">
                                Laboratories ordering more than 25 vials per month qualify for volume pricing.
                                Mention "Bulk / wholesale order" in your subject line.
                            </p>
                        </div>
                    </aside>
                </div>
            </div>
        </div>
    );
}

function InfoCard({ tile, icon: Icon, label, value, sub }) {
    return (
        <div className={`rounded-[22px] p-6 ${tile}`}>
            <Icon size={18} className="mb-3" />
            <div className="data-label mb-1">{label}</div>
            <div className="font-display font-bold text-lg text-[var(--text-primary)]">{value}</div>
            {sub && <div className="text-xs text-secondary mt-2 leading-relaxed">{sub}</div>}
        </div>
    );
}
